import { memo } from "react";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
    Grid,
    InputLabel,
    Switch,
    Divider,
    Link,
    Chip,
    CircularProgress,
    Tooltip,
    Button,
    TextField,
    Typography,
    Paper,
} from "@mui/material";
import useEntitlements from "@/auth/useEntitlements";
import FilesUploader from "@/common/FilesUploader";
import { SectionTitle } from "@/common/typography";
import {
    FEATURE_ACCOUNTING_STRATEGY,
    FEATURE_STORAGE_STRATEGY,
    FEATURE_VENDORS,
} from "@/constants";
import ProjectBidCreate from "./ProjectBidCreate";
import ProjectBids from "./ProjectBids";

const ProjectBidsSection = memo(function ProjectBidsSection({
    project,
    bids,
    setBids,
    confidentialDataPermission,
    updatePermission,
    updateGoogleIds,
    isBidRefreshing,
    refreshProjectBidData,
    handleActualizeOpen,
    canActualize,
    combinedFiles,
    filesPermission,
    handleInputChange,
    theme,
    handlePopupOpen,
}) {
    const { hasFeature } = useEntitlements();

    return (
        <>
            <Grid container width={"100%"} justifyContent={"space-between"}>
                <Grid item>
                    <SectionTitle>Bid Sheets</SectionTitle>
                </Grid>
                {confidentialDataPermission ? (
                    project.legacy_bid ? (
                        <Grid item container columnSpacing={3}>
                            <Grid item>
                                {isBidRefreshing ? <CircularProgress /> : null}
                            </Grid>
                            <Grid item>
                                <Button
                                    onClick={refreshProjectBidData}
                                    disabled={isBidRefreshing}
                                >
                                    Refresh Bid Data
                                </Button>
                            </Grid>
                            <Grid item>
                                <ProjectBidCreate project={project} />
                            </Grid>
                        </Grid>
                    ) : bids?.length > 0 ? (
                        <Grid item>
                            <Grid container item spacing={1}>
                                <Grid item>
                                    {hasFeature(FEATURE_VENDORS) && (
                                        <Tooltip
                                            variant="contained"
                                            title={
                                                project.confirmed_bid
                                                    ? ""
                                                    : "Your bid needs to be signed to start actualizing"
                                            }
                                        >
                                            <Button
                                                variant="outlined"
                                                onClick={handleActualizeOpen}
                                                data-testid="actualize-bid"
                                                disabled={!canActualize}
                                            >
                                                Actualize
                                            </Button>
                                        </Tooltip>
                                    )}
                                </Grid>
                                <Grid item>
                                    <ProjectBidCreate project={project} />
                                </Grid>
                            </Grid>
                        </Grid>
                    ) : (
                        <Grid item>
                            <ProjectBidCreate project={project} />
                        </Grid>
                    )
                ) : null}
            </Grid>

            {bids && bids?.length > 0 ? (
                <>
                    <ProjectBids
                        bids={bids}
                        setBids={setBids}
                        projectCurrency={project.currency}
                        confidentialDataPermission={confidentialDataPermission}
                        updatePermission={updatePermission}
                    />
                    {hasFeature(FEATURE_STORAGE_STRATEGY) && (
                        <Grid spacing={3} container>
                            <Grid item xs={12}>
                                <FilesUploader
                                    disabled
                                    files={combinedFiles}
                                    modelId={project.id}
                                    modelType={"project"}
                                />
                            </Grid>
                        </Grid>
                    )}
                </>
            ) : project.legacy_bid ? (
                <>
                    {project.google_drive_bid_file_id == "n/a" ? (
                        <>
                            {
                                "There's no bid sheet associated with this project. Let ops team know if you think it's a mistake."
                            }
                        </>
                    ) : !project.google_drive_bid_file_id ? (
                        <>
                            {
                                "Your project folder might still be creating, try refreshing in a few seconds :)"
                            }
                        </>
                    ) : null}

                    <Grid spacing={3} container>
                        <Grid item xs={12} md={6}>
                            <SectionTitle>Details</SectionTitle>
                            <Grid
                                container
                                xs={12}
                                md={12}
                                justifyContent={"space-between"}
                                alignItems={"center"}
                            >
                                <Grid padding={0}>
                                    <InputLabel>Approved by EP?</InputLabel>
                                </Grid>
                                <Grid>
                                    <Switch
                                        disabled
                                        checked={
                                            project.ep_approved ? true : false
                                        }
                                    />
                                </Grid>
                                <Divider sx={{ width: "100%" }} />
                                <Grid padding={0}>
                                    <InputLabel>Signed by Client?</InputLabel>
                                </Grid>
                                <Grid>
                                    <Switch
                                        disabled
                                        checked={project.signed ? true : false}
                                    />
                                </Grid>

                                <Divider sx={{ width: "100%" }} />

                                {updateGoogleIds ? (
                                    <>
                                        <Grid item paddingLeft={0}>
                                            <InputLabel>Bid Sheet</InputLabel>
                                        </Grid>
                                        <Grid item flexGrow={1}>
                                            <TextField
                                                value={
                                                    project.google_drive_bid_file_id ||
                                                    ""
                                                }
                                                onChange={handleInputChange(
                                                    "google_drive_bid_file_id",
                                                )}
                                                fullWidth
                                            />
                                        </Grid>
                                        <Divider sx={{ width: "100%" }} />
                                    </>
                                ) : null}

                                {project.google_drive_bid_file_id ? (
                                    <Grid paddingLeft={0}>
                                        <Link
                                            target={"_blank"}
                                            href={`https://docs.google.com/spreadsheets/d/${project.google_drive_bid_file_id}`}
                                            color="inherit"
                                        >
                                            Bid Sheet on Google{" "}
                                            <OpenInNewIcon
                                                sx={{
                                                    verticalAlign: "middle",
                                                    marginLeft: "6px",
                                                }}
                                            />
                                        </Link>
                                    </Grid>
                                ) : null}
                            </Grid>
                        </Grid>
                        {hasFeature(FEATURE_STORAGE_STRATEGY) && (
                            <Grid item xs={12} md={6}>
                                <FilesUploader
                                    files={project.files || []}
                                    modelId={project.id}
                                    modelType={"project"}
                                    disabled={!filesPermission}
                                />
                            </Grid>
                        )}
                    </Grid>
                </>
            ) : updatePermission ? (
                <Grid container justifyContent="center">
                    <Paper
                        sx={{
                            boxShadow: "none",
                            mt: 3,
                            padding: 3,
                            width: "100%",
                            textAlign: "center",
                        }}
                    >
                        <Typography>No bid sheets</Typography>
                    </Paper>
                    {/* <Grid item md={3}>
                        <Button
                            endIcon={<ArrowCircleRightIcon />} // TODO: Replace Icon from Solar
                            onClick={handlePopupOpen}
                            color="secondary"
                        >
                            Create a bid sheet
                        </Button>
                    </Grid> */}
                </Grid>
            ) : (
                <p>None</p>
            )}
        </>
    );
});

ProjectBidsSection.displayName = "ProjectBidsSection";

export default ProjectBidsSection;
